/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAllResourceChanges } from "../../slices/resourceChangeReport/resourceChangeReportSlice";
import { resourceUpdated, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import updateChangeReport from "../../slices/resourceChangeReport/updateChangeReport";
import updateElement, { updateNarrativeElement } from "../../functions/updateElement";
import generateNarrativeForResearchStudyResource from "../generateNarrativeFunctions/generateNarrativeForResearchStudyResource"
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import MarkdownEntry from "../../datatypeComponents/MarkdownEntry";
import StringEntry from "../../datatypeComponents/StringEntry";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import SEVCO, { studyDesignCodeableConceptSet } from "../../dictionaries/SEVCO";

const researchStudyDotPhaseValueSet = [
    { system: "http://hl7.org/fhir/research-study-phase", code: "n-a", display: "N/A" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "early-phase-1", display: "Early Phase 1" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-1", display: "Phase 1" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-1-phase-2", display: "Phase 1/Phase 2" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-2", display: "Phase 2" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-2-phase-3", display: "Phase 2/Phase 3" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-3", display: "Phase 3" },
    { system: "http://hl7.org/fhir/research-study-phase", code: "phase-4", display: "Phase 4" }
];

const ResearchStudyResourceEdit = ({ foi }) => {
    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({
        text: resourceJson.text,
        title: resourceJson.title,
        name: resourceJson.name,
        descriptionSummary: resourceJson.descriptionSummary,
        description: resourceJson.description,
        phase: resourceJson.phase,
        studyDesign: resourceJson.studyDesign,
        relatesTo: resourceJson.relatesTo,
        note: resourceJson.note
    });

    useEffect(() => {
        if (resourceJson) {
            updateNarrativeElement(editResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.text]);

    useEffect(() => {
        if (editResourceState) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.name;
            if (editResourceState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.title)) {
                console.log("resourceUpdate for ResearchStudy.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "title",
                    previousInitialValue,
                    editResourceState.title);
            }
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = editResourceState.name;
            newResourceDetail.displayTitle = editResourceState.title || editResourceState.name;
            if (editResourceState.name !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.name)) {
                console.log("resourceUpdate for ResearchStudy.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "name",
                    previousInitialValue,
                    editResourceState.name);
            }
        }
    }, [editResourceState.name]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "descriptionSummary", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.descriptionSummary]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.description]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "phase", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.phase]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "studyDesign", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.studyDesign]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "relatesTo", "ArrayOfRelatesTo", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.relatesTo]);

    useEffect(() => {
        if (editResourceState) {
            updateElement(editResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [editResourceState.note]);

    return <div>
        {editResourceState && <>
            <StringEntry elementName='title'
                fieldLabel={'Study Title'}
                startingValue={editResourceState.title || ""}
                required={(editResourceState.description || editResourceState.descriptionSummary || editResourceState.phase || editResourceState.studyDesign || editResourceState.relatesTo || editResourceState.note)}
                setResourceState={setEditResourceState} />
            <StringEntry elementName='name' fieldLabel='Study Name (for machine use)'
                startingValue={editResourceState.name || ""} setResourceState={setEditResourceState} />
            <p>Narrative Summary</p>
            <div style={{ marginLeft: "24px" }}>
                <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                    generatedNarrative={generateNarrativeForResearchStudyResource(resourceJson)} startCollapsed
                    startingValue={editResourceState.text} setResourceState={setEditResourceState} />
            </div>
            <MarkdownEntry elementName='descriptionSummary' fieldLabel={'Brief Study Description'}
                startingValue={editResourceState.descriptionSummary || ""} setResourceState={setEditResourceState} />
            <MarkdownEntry elementName='description' fieldLabel={'Long Study Description'}
                startingValue={editResourceState.description || ""} setResourceState={setEditResourceState} />
            <CodeableConceptEntry elementName='phase' fieldLabel='Phase'
                valueSet={researchStudyDotPhaseValueSet}
                startingValue={editResourceState.phase} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='CodeableConcept'
                elementName='studyDesign' fieldLabel='Study Design'
                valueSet={SEVCO.studyDesign} codeableConceptLevelValueSet={studyDesignCodeableConceptSet}
                startingValue={editResourceState.studyDesign} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='RelatesTo'
                elementName='relatesTo' fieldLabel='Related Item'
                startingValue={editResourceState.relatedArtifact} setResourceState={setEditResourceState} />
            <ArrayEntry datatype='Annotation'
                elementName='note' fieldLabel='Note'
                startingValue={editResourceState.note} setResourceState={setEditResourceState} />
            <br />
        </>}
    </div>
}

export default ResearchStudyResourceEdit;